// @flow

import React, { useState, Element } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { get } from 'lodash';
import { Tabs, Tab, Menu, MenuItem, withStyles } from '@material-ui/core';
import Button from '../../../../components/Button';
import { ReactComponent as BackIcon } from '@assets/icons/back.svg';
import { ReactComponent as UserIcon } from '@assets/icons/gray_user.svg';
import { ReactComponent as EditModeIcon } from '@assets/icons/inactive_edit_mode.svg';
import { ReactComponent as Arrow } from '@assets/icons/small_arrow.svg';
import styles from './MainMenu.scss';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

type Item = {
  value: string,
  wrapped: boolean
}

type PropsType = {
  orientation: String,
  classes?: Object,
  items: Item[],
  isLogin?: Boolean
}

const HOME_LINK = '/wiki';
const ABOUT_LINK = '/portal/longread/132';

const CustomTab = withStyles({
  root: {
    minWidth: 'initial',
    color: '#1D1D1B',
    fontSize: '14px',
    lineHeight: '18px',
    padding: '8px 16px',
    minHeight: '64px',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
  },
  textColorInherit: {
    opacity: 1,
  },
})(Tab);

const CustomMenu = withStyles({
  paper: {
    borderRadius: '0 0 6px 6px',
    boxShadow:
      '0px 8px 16px -6px rgba(24, 39, 75, 0.08), 0px 6px 8px -6px rgba(24, 39, 75, 0.12)',
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
})(Menu);

const CustomMenuItem = withStyles({
  root: {
    fontFamily: 'Kaspersky Sans Display, sans-serif',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '16px',
    color: '#1D1D1B',
    padding: '12px 16px',
    '&:hover': {
      background: 'rgba(41, 77, 117, 0.09)',
    },
  },
})(MenuItem);

const MainMenu = ({ orientation, classes, items, isLogin }: PropsType): Element<'div'> => {
  const isMatchHome = get(useRouteMatch(HOME_LINK), 'isExact');
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const { isAd } = useSelector(state => get(state, 'session'));

  const userHasAccessToInternalTools = () => {
    return isAd;
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isVertical = orientation === 'vertical';

  return (
    <>
      <Tabs
        variant="standard"
        orientation={orientation}
        className={classes.root}
        classes={{ root: styles.Tabs }}
        value={false}
      >
        {
          (isVertical && !isMatchHome) ? [
            <Tab
              classes={{ root: styles.TabHome }}
              icon={<BackIcon />}
              label="Home"
              selected={false}
              wrapped={false}
              href={HOME_LINK}
              key="home"
            />,
            items.map((item: Item, index: number): Element<Tab> =>
              (
                <Tab
                  component="a"
                  classes={{
                    root: classNames({
                      [styles.Tab]: true,
                      // [styles.Selected]: item.link === pathname
                    }),
                  }}
                  label={item.title}
                  href={item.link}
                  key={`${item.link + index}`}
                />
              ),
            ),
          ] : [
            <Tab
              component="a"
              classes={{
                root: classNames({
                  [styles.Tab]: true,
                  [styles.Selected]: !isVertical,
                }),
              }}
              label="Wiki"
              href={HOME_LINK}
              key="wiki"
            />,
            <Tab
              component="a"
              classes={{ root: styles.Tab }}
              label="Homepage"
              href="/portal/"
              key="homepage"
            />,
            <Tab
              component="a"
              classes={{ root: styles.Tab }}
              label="All assets"
              href="/portal/assets"
              key="assets"
            />,
            isLogin && !isVertical && [
              <CustomTab
                classes={{ root: styles.Tab }}
                label={
                  <>
                    <span>Tools</span>
                    <Arrow />
                  </>
                }
                onClick={handleClick}
                key="tools"
              />,
              <CustomMenu
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                disableScrollLock
                key="tools_menu"
              >
                {userHasAccessToInternalTools() && (
                  <CustomMenuItem
                    onClick={handleClose}
                    component="a"
                    href="/portal/business_card"
                  >
                    Digital Business Card
                  </CustomMenuItem>
                )}
                <CustomMenuItem
                  onClick={handleClose}
                  component="a"
                  href="/portal/qr_code"
                >
                  Custom QR Code
                </CustomMenuItem>
                {userHasAccessToInternalTools() && (
                  <CustomMenuItem
                    onClick={handleClose}
                    component="a"
                    href="/portal/email_signature"
                  >
                    Email Signature Generator
                  </CustomMenuItem>
                )}
              </CustomMenu>,
            ],
            userHasAccessToInternalTools() && isVertical && [
              <Tab
                component="a"
                classes={{ root: styles.Tab }}
                label="Digital Business Card"
                href="/portal/business_card"
                key="business_card"
              />,
            ],
            isLogin && isVertical && [
              <Tab
                component="a"
                classes={{ root: styles.Tab }}
                label="Custom QR Code"
                href="/portal/qr_code"
                key="qr_code"
              />,
            ],
            userHasAccessToInternalTools() && isVertical && [
              <Tab
                component="a"
                classes={{ root: styles.Tab }}
                label="Email Signature Generator"
                href="/portal/email_signature"
                key="email_signature"
              />,
            ],
            <Tab
              component="a"
              classes={{ root: styles.Tab }}
              label="About"
              href={ABOUT_LINK}
              key="about"
            />,
          ]
        }
      </Tabs>
      {
        isVertical && isLogin &&
        (
          <div className={styles.SubMenu}>
            {/* <Button
              size="small"
              startIcon={<UserIcon />}
              to="/login"
            >
              <span>User Profile</span>
            </Button> */}
            <Button
              size="small"
              startIcon={<EditModeIcon />}
            >
              <span>
                Edit mode
                <span>(Not available at mobile version)</span>
              </span>
            </Button>
          </div>
        )
      }
    </>
  );
};

export default MainMenu;
